import axios from "axios";
import { API_BASE_URL, getAuthToken } from "./shared";

// Upload helper for the Artist Studio screens. Same two-step flow as the rest of
// the app (ask the API for a presigned S3 link, then PUT the file to it), but:
//  - file names are made URL-safe first (spaces and symbols in a name such as
//    "My Photo (1).jpg" would otherwise produce an image link the API rejects)
//  - failures say which step broke, so the toast is actionable.
export const safeFileName = (name) => {
  const dot = name.lastIndexOf(".");
  const base = (dot > 0 ? name.slice(0, dot) : name).replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || "file";
  const ext = dot > 0 ? name.slice(dot).toLowerCase().replace(/[^a-z0-9.]/g, "") : "";
  return `${base}${ext}`;
};

export class UploadError extends Error {}

export default async function uploadStudioFiles(files, { onProgress } = {}) {
  if (!files || !files.length) throw new UploadError("No file selected.");
  const renamed = files.map((f) => new File([f], safeFileName(f.name), { type: f.type }));

  let directory;
  try {
    const response = await axios.post(
      `${API_BASE_URL}/aws/generate-presigned-url`,
      { files: renamed.map((f) => ({ fileName: f.name, fileType: f.type })) },
      { headers: { token: getAuthToken() }, timeout: 20000 }
    );
    if (!response.data?.success || !Array.isArray(response.data.directory)) {
      throw new UploadError("The server did not return an upload link.");
    }
    directory = response.data.directory;
  } catch (error) {
    if (error instanceof UploadError) throw error;
    if (!error.response) {
      throw new UploadError(`Can't reach the server at ${API_BASE_URL || "the API"}. Check that pipaan-node is running.`);
    }
    throw new UploadError(error.response.data?.message || `The server refused the upload request (${error.response.status}).`);
  }

  try {
    const totalBytes = renamed.reduce((sum, f) => sum + f.size, 0) || 1;
    const sent = renamed.map(() => 0);
    await Promise.all(
      directory.map((item, index) =>
        axios.put(item.presignedUrl, renamed[index], {
          headers: { "Content-Type": renamed[index].type },
          timeout: 60000,
          onUploadProgress: (e) => {
            if (!onProgress) return;
            sent[index] = Math.min(e.loaded || 0, renamed[index].size);
            onProgress(Math.min(1, sent.reduce((a, b) => a + b, 0) / totalBytes));
          },
        })
      )
    );
    if (onProgress) onProgress(1);
  } catch (error) {
    if (!error.response) {
      throw new UploadError(
        `The image storage blocked the upload. Its CORS settings must allow ${window.location.origin}, or you may be offline.`
      );
    }
    throw new UploadError(`The image storage rejected the file (${error.response.status}). Please try again.`);
  }

  return directory.map((item) => ({
    media_type: item.fileType.startsWith("image") ? "image" : "pdf",
    url: item.fileUrl,
  }));
}
